import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import AddressAutocomplete from '../common/AddressAutocomplete';
import { EmailField, PhoneField } from '../common/ValidatedFields';
import { Typography, Button, Snackbar, Alert, AlertTitle, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import ThemeBackground from '../Layout/ThemeBackground';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { isCancel } from 'axios';
import { fetchAssessmentDraft, saveAssessmentDraft, deleteAssessmentDraft } from '../../api/assessmentDrafts';
import '../../index.css';

interface Lookup {
  _id: string;
  category: string;
  value: string;
  displayName: string;
  active: boolean;
  sessionRate?: number;
  numberOfSessions?: number;
}

interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

interface ClientDetails {
  email: string;
  dob: string;
  street: string;
  city: string;
  postcode: string;
  county?: string;
  country?: string;
  address: string; // Keep for backward compatibility
  initialAssessment: string;
  assessmentDateTime: string;
  rescheduleAssessmentDateTime: string;
  rescheduleNotes: string;
  organisation: string;
  numberOfSessions: string;
  sessionRate: string;
  firstName: string;
  lastName: string;
  phone: string;
  gender: string;
  assessmentDate: string;
  assessmentTime: string;
  assessmentStatus: string;
  referralCategory: string;
  referralType: string;
  referralTypeDisplayName: string;
  organisationDisplayName: string;
  gpPractice: string;
  gpAddress: string;
  gpAddressLine2: string;
  gpAddressLine3: string;
  gpCity: string;
  gpCounty: string;
  gpPostcode: string;
  gpCountry: string;
  gpPhone: string;
  emergencyContacts: EmergencyContact[];
}

interface Errors {
  email?: string;
  age?: string;
  street?: string;
  city?: string;
  postcode?: string;
  gpPhone?: string;
  emergencyContacts?: string;
  submit?: string;
  assessmentStatus?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  gender?: string;
  referralType?: string;
  referralCategory?: string;
  organisation?: string;
  organisationDisplayName?: string;
  dob?: string;
}

interface Month {
  value: string;
  label: string;
}

interface FormEvent extends React.ChangeEvent<HTMLElement> {
  target: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
}

const AssessmentPart1: React.FC = () => {
  const { user } = useAuth();
  const [clientDetails, setClientDetails] = React.useState<ClientDetails>({
    email: '',
    dob: '',
    street: '',
    city: '',
    postcode: '',
    address: '',
    initialAssessment: '',
    assessmentDateTime: '',
    rescheduleAssessmentDateTime: '',
    rescheduleNotes: '',
    organisation: '',
    numberOfSessions: '',
    sessionRate: '',
    firstName: '',
    lastName: '',
    phone: '',
    gender: '',
    assessmentDate: '',
    assessmentTime: '',
    assessmentStatus: 'not_started',
    referralCategory: '', 
    referralType: '', 
    referralTypeDisplayName: '', 
    organisationDisplayName: '',
    gpPractice: '',
    gpAddress: '',
    gpAddressLine2: '',
    gpAddressLine3: '',
    gpCity: '',
    gpCounty: '',
    gpPostcode: '',
    gpCountry: '',
    gpPhone: '',
    emergencyContacts: []
  });
  const [day, setDay] = React.useState<string>('');
  const [month, setMonth] = React.useState<string>('');
  const [year, setYear] = React.useState<string>('');
  const [calculatedAge, setCalculatedAge] = React.useState<string>('');
  const [readyToProceed, setReadyToProceed] = React.useState<boolean | null>(null);
  const [wantsToReschedule, setWantsToReschedule] = React.useState<boolean | null>(null);
  const [wantsToOffboard, setWantsToOffboard] = React.useState<boolean | null>(null);
  const [genderTypes, setGenderTypes] = React.useState<Lookup[]>([]);
  const [errors, setErrors] = React.useState<Errors>({});
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [apiError, setApiError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [showRetryButton, setShowRetryButton] = React.useState(false);
  const [savedClientData, setSavedClientData] = React.useState<any>(null);
  const [showRestoreDraftDialog, setShowRestoreDraftDialog] = React.useState(false);
  const [savedDraft, setSavedDraft] = React.useState<ClientDetails | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const isRestoringDraft = React.useRef(false);

  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const months: Month[] = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];
  
  const years: number[] = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  const firstNameRef = React.createRef<HTMLInputElement>();
  const lastNameRef = React.createRef<HTMLInputElement>();
  const gpPhoneRef = React.createRef<HTMLInputElement>();
  const genderRef = React.createRef<HTMLSelectElement>();

  const calculateAge = React.useCallback((): number | null => {
    if (!day || !month || !year) return null;
    const birthDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }, [day, month, year]);

  React.useEffect(() => {
    setCalculatedAge(calculateAge()?.toString() || '');
  }, [day, month, year, calculateAge]);

  React.useEffect(() => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchData = async () => {
      setIsLoading(true);
      setApiError(null);
      try {
        const [clientResponse, genderResponse, referralTypesResponse] = await Promise.all([
          api.get(`/api/clients/${clientId}`, { signal }),
          api.get('/api/lookups/public/gender', { signal }),
          api.get('/api/lookups/admin/referralTypes', { signal })
        ]);

        if (!clientResponse.data) {
          console.error('No client data received');
          setApiError('Failed to fetch client data');
          return;
        }

        console.log('Initial client data:', clientResponse.data);

        if (typeof clientResponse.data !== 'object' || Array.isArray(clientResponse.data)) {
          console.error('Invalid client data format:', clientResponse.data);
          setApiError('Invalid client data format received');
          return;
        }

        const data = clientResponse.data;

        // Get the referral type display name from the lookup
        const referralTypes = referralTypesResponse.data || [];
        const matchingReferralType = referralTypes.find((type: Lookup) => type.value === data.referralType);
        const referralTypeDisplayName = matchingReferralType?.displayName || '';

        // Log the referral data specifically
        console.log('Referral data:', {
          referralType: data.referralType,
          referralCategory: data.referralCategory,
          referralTypeDisplayName: referralTypeDisplayName
        });

        setClientDetails(prevState => {
          const newState = {
            ...prevState,
            email: data.email || '',
            dob: data.dob || '',
            street: data.street || '',
            city: data.city || '',
            postcode: data.postcode || '',
            address: data.address || '', // Keep for backward compatibility
            initialAssessment: data.initialAssessment || '',
            assessmentDateTime: data.assessmentDateTime || '',
            rescheduleAssessmentDateTime: '',
            rescheduleNotes: data.rescheduleNotes || '',
            organisation: data.organisation || '',
            numberOfSessions: data.numberOfSessions || '',
            sessionRate: data.sessionRate || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phone: data.phone || '',
            gender: data.gender || '',
            assessmentDate: data.assessmentDate || '',
            assessmentTime: data.assessmentTime || '',
            assessmentStatus: data.assessmentStatus || 'not_started',
            referralCategory: data.referralCategory || '', 
            referralType: data.referralType || '', 
            referralTypeDisplayName: referralTypeDisplayName || '',
            organisationDisplayName: data.organisationDisplayName || '',
            gpPractice: data.gpPractice || '',
            gpAddress: data.gpAddress || '',
            gpAddressLine2: data.gpAddressLine2 || '',
            gpAddressLine3: data.gpAddressLine3 || '',
            gpCity: data.gpCity || '',
            gpCounty: data.gpCounty || '',
            gpPostcode: data.gpPostcode || '',
            gpCountry: data.gpCountry || '',
            gpPhone: data.gpPhone || '',
            emergencyContacts: data.emergencyContacts || []
          };
          
          // Log the state after update
          console.log('Setting client details:', newState);
          
          return newState;
        });

        if (data.dob) {
          const birthDate = new Date(data.dob);
          setYear(birthDate.getFullYear().toString());
          setMonth((birthDate.getMonth() + 1).toString().padStart(2, '0'));
          setDay(birthDate.getDate().toString().padStart(2, '0'));
        }

        setGenderTypes(genderResponse.data || []);

      } catch (error) {
        if (!isCancel(error)) {
          console.error('Error fetching data:', error);
          setApiError('Failed to load assessment data. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (clientId) {
      fetchData();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [clientId]);

  // Phase 2C, Stage 6: drafts are now encrypted and stored server-side
  // (design §9, resolving CLIENT-08), scoped to this assessor and this
  // client - replacing the previous plaintext browser localStorage
  // drafts. Server-side TTL (7 days) and immediate deletion on successful
  // submission are handled by the backend; the UX (restore/discard
  // dialog) is unchanged.

  // Check for a saved draft on mount
  React.useEffect(() => {
    if (!clientId) return;

    const checkForDraft = async () => {
      try {
        const draft = await fetchAssessmentDraft<typeof clientDetails>(clientId, 'part1');
        if (draft) {
          setSavedDraft(draft.content);
          setShowRestoreDraftDialog(true);
        }
      } catch (error) {
        console.error('Error checking for draft:', error);
      }
    };

    // Delay check to allow initial data load
    const timer = setTimeout(checkForDraft, 1000);
    return () => clearTimeout(timer);
  }, [clientId]);

  // Auto-save draft to the server
  React.useEffect(() => {
    if (!clientId || isRestoringDraft.current) return;

    // Don't save if form is empty (initial state)
    const hasData = clientDetails.firstName?.trim() || 
                    clientDetails.lastName?.trim() || 
                    clientDetails.phone?.trim() ||
                    clientDetails.email?.trim() ||
                    clientDetails.address?.trim();

    if (!hasData) return;

    // Debounce the save
    const timer = setTimeout(() => {
      saveAssessmentDraft(clientId, 'part1', clientDetails).catch((error) => {
        console.error('Error saving draft:', error);
      });
    }, 1000); // Save 1 second after user stops typing

    return () => clearTimeout(timer);
  }, [clientDetails, clientId]);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone: string): boolean => {
    // Remove all non-digit characters
    const cleanedPhone = phone.replace(/\D/g, '');
    
    // Accept phone numbers that are:
    // - At least 10 digits (standard UK/US number)
    // - At most 15 digits (maximum length for international numbers)
    return cleanedPhone.length >= 10 && cleanedPhone.length <= 15;
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    // Validate required fields
    if (!clientDetails.firstName?.trim()) {
      newErrors.firstName = 'First Name is required';
    }

    if (!clientDetails.lastName?.trim()) {
      newErrors.lastName = 'Last Name is required';
    }

    if (!clientDetails.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(clientDetails.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Validate Email (mandatory)
    if (!clientDetails.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(clientDetails.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate Gender (mandatory)
    if (!clientDetails.gender?.trim()) {
      newErrors.gender = 'Gender is required';
    }

    // Validate Age (mandatory)
    if (!calculatedAge && (!day || !month || !year)) {
      newErrors.dob = 'Date of birth is required';
    } else if (calculatedAge && Number(calculatedAge) < 18) {
      newErrors.age = 'Client must be at least 18 years old';
    }

    // Validate Address fields (mandatory)
    if (!clientDetails.street?.trim()) {
      newErrors.street = 'Street address is required';
    }

    if (!clientDetails.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!clientDetails.postcode?.trim()) {
      newErrors.postcode = 'Postcode is required';
    }

    // Validate GP phone if provided
    if (clientDetails.gpPhone && !validatePhone(clientDetails.gpPhone)) {
      newErrors.gpPhone = 'Please enter a valid phone number';
    }

    // Validate emergency contacts (at least one required)
    if (clientDetails.emergencyContacts.length === 0) {
      newErrors.emergencyContacts = 'At least one emergency contact is required';
    } else {
      // Validate each emergency contact
      const hasInvalidContact = clientDetails.emergencyContacts.some(contact => {
        return !contact.name?.trim() || !contact.phone?.trim() || !contact.relationship?.trim();
      });
      
      if (hasInvalidContact) {
        newErrors.emergencyContacts = 'All emergency contact fields must be completed';
      }
      
      // Validate phone numbers
      const hasInvalidPhone = clientDetails.emergencyContacts.some(contact => {
        return contact.phone && !validatePhone(contact.phone);
      });
      
      if (hasInvalidPhone) {
        newErrors.emergencyContacts = 'Please enter valid phone numbers for all emergency contacts';
      }
    }

    // Only validate if we're proceeding to Part 2 (not offboarding)
    if (readyToProceed) {
      // Validate referral category first
      if (!clientDetails.referralCategory || clientDetails.referralCategory.trim() === '') {
        newErrors.referralCategory = 'Referral category is required';
      } else {
        // For organization referrals
        if (clientDetails.referralCategory === 'organisation') {
          if (!clientDetails.organisation || clientDetails.organisation.trim() === '') {
            newErrors.organisation = 'Organization is required';
          }
          if (!clientDetails.organisationDisplayName || clientDetails.organisationDisplayName.trim() === '') {
            newErrors.organisationDisplayName = 'Organization name is required';
          }
        } else {
          // For other referral types
          if (!clientDetails.referralType || clientDetails.referralType.trim() === '') {
            newErrors.referralType = 'Referral type is required';
          }
        }
      }
    }

    setErrors(newErrors);
    console.log('Validation errors:', newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: FormEvent): void => {
    const { name, value } = e.target;
    console.log(`Updating ${name} with value:`, value);
    
    // Special handling for GP fields
    if (name === 'gpPractice' || name === 'gpAddress' || name === 'gpPhone') {
      console.log(`Updating ${name} field with:`, value);
    }
    
    setClientDetails(prevState => {
      const newState = {
        ...prevState,
        [name]: value
      };
      console.log('New client details state:', newState);
      return newState;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError(null);
    setErrors({});
    setShowRetryButton(false);

    try {
      if (!clientId) {
        setSnackbar({
          open: true,
          message: 'No client ID provided. Please try again.',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }

      // Check if user has selected an action
      if (!readyToProceed && !wantsToReschedule && !wantsToOffboard) {
        setSnackbar({
          open: true,
          message: 'Please select an action: Proceed to Part 2, Reschedule, or Offboard',
          severity: 'warning'
        });
        setErrors({ submit: 'Please select an action before submitting' });
        setIsSubmitting(false);
        return;
      }

      // Validate based on action
      if (readyToProceed) {
        // Full validation for proceeding to Part 2
        if (!validateForm()) {
          setSnackbar({
            open: true,
            message: 'Please fix the validation errors before submitting.',
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }
      } else if (wantsToReschedule) {
        // Only validate reschedule fields
        if (!clientDetails.rescheduleAssessmentDateTime || !clientDetails.rescheduleNotes) {
          setSnackbar({
            open: true,
            message: 'Please provide reschedule date/time and notes',
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }
        
        // Validate that reschedule date is in the future
        const rescheduleDate = new Date(clientDetails.rescheduleAssessmentDateTime);
        const now = new Date();
        
        if (rescheduleDate <= now) {
          setSnackbar({
            open: true,
            message: 'Reschedule date must be in the future',
            severity: 'error'
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Debug log to check client details before update
      console.log('Client details before update:', clientDetails);

      // Create the update payload
      const updatedClient = {
        ...clientDetails,
        dob: year && month && day ?
          `${year}-${month}-${day}` :
          clientDetails.dob,
        // Include split address fields
        street: clientDetails.street,
        city: clientDetails.city,
        postcode: clientDetails.postcode,
        referralType: clientDetails.referralCategory === 'organisation' ? 
          clientDetails.organisation : 
          clientDetails.referralType,
        referralCategory: clientDetails.referralCategory,
        referralTypeDisplayName: clientDetails.referralCategory === 'organisation' ? 
          clientDetails.organisationDisplayName : 
          clientDetails.referralTypeDisplayName,
        sessionRate: clientDetails.sessionRate || '0',
        // Explicitly include these fields
        gpPractice: clientDetails.gpPractice,
        gpAddress: clientDetails.gpAddress,
        gpAddressLine2: clientDetails.gpAddressLine2,
        gpAddressLine3: clientDetails.gpAddressLine3,
        gpCity: clientDetails.gpCity,
        gpCounty: clientDetails.gpCounty,
        gpPostcode: clientDetails.gpPostcode,
        gpCountry: clientDetails.gpCountry,
        gpPhone: clientDetails.gpPhone,
        emergencyContacts: clientDetails.emergencyContacts
      };

      // Debug log to check the update payload
      console.log('Update payload with GP and emergency contact:', updatedClient);

      // Phase 6 (decision 6): the request expresses a business action and
      // the server decides the resulting assessmentStatus - the client no
      // longer writes lifecycle values directly. (These mutations also used
      // to run AFTER the PUT below, so they never reached the server.)
      if (wantsToReschedule && clientDetails.rescheduleAssessmentDateTime) {
        const rescheduleDate = new Date(clientDetails.rescheduleAssessmentDateTime);
        updatedClient.assessmentDateTime = clientDetails.rescheduleAssessmentDateTime;
        updatedClient.assessmentDate = rescheduleDate.toLocaleDateString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        updatedClient.assessmentTime = rescheduleDate.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        updatedClient.rescheduleNotes = clientDetails.rescheduleNotes;
        (updatedClient as any).action = 'reschedule';
      } else if (readyToProceed) {
        (updatedClient as any).action = 'proceed';
      }
      // wantsToOffboard deliberately sends no lifecycle value - the
      // dedicated offboarding flow (next screen) owns that transition.

      // Make the API call and log the response
      const response = await api.put(`/api/clients/${clientId}`, updatedClient);
      console.log('API response:', response.data);

      if (response.status === 200) {
        // Save client data for potential retry
        setSavedClientData(updatedClient);

        try {
          // Create calendar event for the assessment
          const assessmentDate = new Date(updatedClient.assessmentDateTime);
          const endDate = new Date(assessmentDate.getTime() + 50 * 60000); // 50 minutes duration

          await api.post('/events', {
            title: `Assessment: ${updatedClient.firstName} ${updatedClient.lastName}`,
            start: assessmentDate,
            end: endDate,
            type: 'assessment',
            assessor: user?._id,
            client: clientId,
            notes: updatedClient.rescheduleNotes || ''
          });

          // Success - show notification and navigate
          // Clear draft on successful submission
          if (clientId) {
            await deleteAssessmentDraft(clientId, 'part1');
          }
          
          setSnackbar({
            open: true,
            message: readyToProceed ? 'Assessment saved successfully!' : 
                     wantsToReschedule ? 'Assessment rescheduled successfully!' :
                     'Client offboarding initiated.',
            severity: 'success'
          });

          setTimeout(() => {
            if (readyToProceed) {
              navigate(`/assessment-part-2/${clientId}`);
            } else if (wantsToReschedule) {
              navigate('/');
            } else if (wantsToOffboard) {
              navigate(`/offboard/client/${clientId}`);
            }
          }, 1500);
        } catch (calendarError: any) {
          console.error('Error creating calendar event:', calendarError);
          const errorMessage = calendarError.response?.data?.message || 
                             calendarError.message || 
                             'Failed to create calendar event';
          
          setSnackbar({
            open: true,
            message: `Assessment saved, but ${errorMessage}. You can create the calendar event manually.`,
            severity: 'warning'
          });
          setShowRetryButton(true);
          setIsSubmitting(false);
          return; // Don't navigate, let user retry
        }
      } else {
        const errorMessage = response.data?.message || 'Failed to update client';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
    } catch (error: any) {
      console.error('Error in form submission:', error);
      
      // Extract detailed error message
      let errorMessage = 'Error saving data. Please try again.';
      
      if (error.response) {
        // Server responded with error
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid data provided. Please check all fields.';
        } else if (error.response.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (error.response.status === 404) {
          errorMessage = 'Client not found. Please refresh the page.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message) {
        // Other errors
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryCalendarEvent = async () => {
    if (!savedClientData || !clientId) return;

    setIsSubmitting(true);
    try {
      const assessmentDate = new Date(savedClientData.assessmentDateTime);
      const endDate = new Date(assessmentDate.getTime() + 50 * 60000);

      await api.post('/events', {
        title: `Assessment: ${savedClientData.firstName} ${savedClientData.lastName}`,
        start: assessmentDate,
        end: endDate,
        type: 'assessment',
        assessor: user?._id,
        client: clientId,
        notes: savedClientData.rescheduleNotes || ''
      });

      setSnackbar({
        open: true,
        message: 'Calendar event created successfully!',
        severity: 'success'
      });
      setShowRetryButton(false);

      setTimeout(() => {
        if (readyToProceed) {
          navigate(`/assessment-part-2/${clientId}`);
        } else if (wantsToReschedule) {
          navigate('/');
        } else if (wantsToOffboard) {
          navigate(`/offboard/client/${clientId}`);
        }
      }, 1500);
    } catch (error: any) {
      console.error('Error retrying calendar event:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create calendar event';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestoreDraft = () => {
    if (savedDraft) {
      isRestoringDraft.current = true;
      setClientDetails(savedDraft);
      setShowRestoreDraftDialog(false);
      setSnackbar({
        open: true,
        message: 'Draft restored successfully! Your previous work has been recovered.',
        severity: 'success'
      });
      // Reset flag after a delay
      setTimeout(() => {
        isRestoringDraft.current = false;
      }, 2000);
    }
  };

  const handleDiscardDraft = () => {
    if (clientId) {
      deleteAssessmentDraft(clientId, 'part1').catch((error) => console.error('Error discarding draft:', error));
    }
    setShowRestoreDraftDialog(false);
    setSavedDraft(null);
    setSnackbar({
      open: true,
      message: 'Draft discarded. Starting with fresh form.',
      severity: 'info'
    });
  };

  const handleAddEmergencyContact = () => {
    setClientDetails(prev => ({
      ...prev,
      emergencyContacts: [
        ...prev.emergencyContacts,
        { name: '', phone: '', relationship: '' }
      ]
    }));
  };

  const handleRemoveEmergencyContact = (index: number) => {
    setClientDetails(prev => ({
      ...prev,
      emergencyContacts: prev.emergencyContacts.filter((_, i) => i !== index)
    }));
  };

  const handleEmergencyContactChange = (index: number, field: keyof EmergencyContact, value: string) => {
    setClientDetails(prev => ({
      ...prev,
      emergencyContacts: prev.emergencyContacts.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                        }
                      }}
                      onClick={() => navigate('/assessor')}
                    >
                      Back to Assessor Page
                    </Button>
                  </div>
                </div>

                {/* Title on the right */}
                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Assessment Part 1
                  </Typography>
                  {clientDetails && (
                    <Typography 
                      variant="subtitle1"
                      className="theme-text-secondary"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }}
                    >
                      {clientDetails.firstName} {clientDetails.lastName}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : apiError ? (
            <div className="text-center p-6 bg-red-50 rounded-lg">
              <p className="text-red-600 mb-4">{apiError}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="mt-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Summary Panel */}
                {Object.keys(errors).length > 0 && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <AlertTitle>Please fix the following errors:</AlertTitle>
                    <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                      {Object.entries(errors).map(([field, message]) => (
                        <li key={field} style={{ marginBottom: '4px' }}>{message}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {/* Personal Information */}
                <div className="glass-card p-6">
                  <div className="flex justify-between items-center mb-4">
                    <Typography 
                      variant="h4" 
                      component="h2" 
                      gutterBottom
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontSize: '1.8rem',
                        color: 'rgba(255, 255, 255, 0.95)',
                        letterSpacing: '0.5px',
                        marginBottom: 0
                      }}
                    >
                      Personal Information
                    </Typography>
                  </div>
                  <div className="glass-card-light p-6 rounded-lg space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* First Name */}
                      <div>
                        <label className="block text-sm font-medium theme-text-primary mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={clientDetails.firstName || ''}
                          onChange={handleInputChange}
                          ref={firstNameRef}
                          className="theme-input"
                        />
                        {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                      </div>

                      {/* Last Name */}
                      <div>
                        <label className="block text-sm font-medium theme-text-primary mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={clientDetails.lastName || ''}
                          onChange={handleInputChange}
                          ref={lastNameRef}
                          className="theme-input"
                        />
                        {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                      </div>

                      {/* Phone */}
                      <div>
                        <PhoneField
                          value={clientDetails.phone || ''}
                          onChange={(value) => setClientDetails(prev => ({ ...prev, phone: value }))}
                          label="Phone"
                          externalError={errors.phone}
                          required
                        />
                      </div>

                      {/* Address Autocomplete */}
                      <div className="col-span-2">
                        <AddressAutocomplete
                          value={{
                            street: clientDetails.street || '',
                            city: clientDetails.city || '',
                            postcode: clientDetails.postcode || '',
                            county: clientDetails.county || '',
                            country: clientDetails.country || ''
                          }}
                          onChange={(address) => {
                            setClientDetails({
                              ...clientDetails,
                              street: address.street || '',
                              city: address.city || '',
                              postcode: address.postcode || '',
                              county: address.county || '',
                              country: address.country || ''
                            });
                            // Clear errors when address is updated
                            setErrors({
                              ...errors,
                              street: undefined,
                              city: undefined,
                              postcode: undefined
                            });
                          }}
                          fieldMapping={{
                            line1: 'street',
                            line2: 'line2',
                            line3: 'line3',
                            city: 'city',
                            postcode: 'postcode',
                            county: 'county',
                            country: 'country'
                          }}
                          required={true}
                          label="Client Address"
                        />
                        {(errors.street || errors.city || errors.postcode) && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.street || errors.city || errors.postcode}
                          </p>
                        )}
                      </div>

                      {/* Gender */}
                      <div>
                        <label className="block text-sm font-medium theme-text-primary mb-1">
                          Gender
                        </label>
                        <select
                          name="gender"
                          value={clientDetails.gender || ''}
                          onChange={handleInputChange}
                          ref={genderRef}
                          className="theme-input"
                        >
                          <option value="">Select Gender</option>
                          {genderTypes.map((gender: Lookup) => (
                            <option key={gender._id} value={gender.value}>
                              {gender.displayName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {/* Date of Birth with Age Display */}
                      <div>
                        <label className="block text-sm font-medium theme-text-primary mb-2">Date of Birth:</label>
                        <div className="flex gap-2">
                          <select 
                            value={day} 
                            onChange={(e) => setDay(e.target.value)} 
                            className="p-2 border rounded"
                            data-field="dob"
                          >
                            <option value="">Day</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                              <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                            ))}
                          </select>
                          <select 
                            value={month} 
                            onChange={(e) => setMonth(e.target.value)} 
                            className="p-2 border rounded"
                            data-field="dob"
                          >
                            <option value="">Month</option>
                            {months.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <select 
                            value={year} 
                            onChange={(e) => setYear(e.target.value)} 
                            className="p-2 border rounded"
                            data-field="dob"
                          >
                            <option value="">Year</option>
                            {years.map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                        {calculatedAge && <p className="mt-2">Age: {calculatedAge} years</p>}
                        {errors.dob && <p className="text-sm text-red-500 mt-1">{errors.dob}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="glass-card rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <Typography 
                      variant="h4" 
                      component="h2" 
                      gutterBottom
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontSize: '1.8rem',
                        color: 'rgba(255, 255, 255, 0.95)',
                        letterSpacing: '0.5px',
                        marginBottom: 0
                      }}
                    >
                      Email
                    </Typography>
                  </div>
                  <div className="glass-card-light p-6 rounded-lg">
                    <div>
                      <EmailField
                        value={clientDetails.email || ''}
                        onChange={(value) => setClientDetails(prev => ({ ...prev, email: value }))}
                        label="Email Address"
                        externalError={errors.email}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* GP Information */}
                <div className="glass-card rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <Typography 
                      variant="h4" 
                      component="h2" 
                      gutterBottom
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontSize: '1.8rem',
                        color: 'rgba(255, 255, 255, 0.95)',
                        letterSpacing: '0.5px',
                        marginBottom: 0
                      }}
                    >
                      GP Information
                    </Typography>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 glass-card-light p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium theme-text-primary mb-2">Practice Name</label>
                      <input
                        type="text"
                        name="gpPractice"
                        value={clientDetails.gpPractice || ''}
                        onChange={(e: FormEvent) => handleInputChange(e)}
                        className="theme-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-primary mb-2">Phone</label>
                      <input
                        type="tel"
                        name="gpPhone"
                        value={clientDetails.gpPhone || ''}
                        onChange={(e: FormEvent) => handleInputChange(e)}
                        ref={gpPhoneRef}
                        className="theme-input"
                      />
                      {errors.gpPhone && (
                        <p className="text-red-500 text-sm mt-1">{errors.gpPhone}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <AddressAutocomplete
                        value={{
                          line1: clientDetails.gpAddress,
                          line2: clientDetails.gpAddressLine2,
                          line3: clientDetails.gpAddressLine3,
                          city: clientDetails.gpCity,
                          county: clientDetails.gpCounty,
                          postcode: clientDetails.gpPostcode,
                          country: clientDetails.gpCountry
                        }}
                        onChange={(address) => {
                          setClientDetails(prev => ({
                            ...prev,
                            gpAddress: address.line1 || '',
                            gpAddressLine2: address.line2 || '',
                            gpAddressLine3: address.line3 || '',
                            gpCity: address.city || '',
                            gpCounty: address.county || '',
                            gpPostcode: address.postcode || '',
                            gpCountry: address.country || ''
                          }));
                        }}
                        required={false}
                        label="GP Practice Address"
                        fieldMapping={{
                          line1: 'line1',
                          line2: 'line2',
                          line3: 'line3',
                          city: 'city',
                          postcode: 'postcode',
                          county: 'county',
                          country: 'country'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Emergency Contacts */}
                <div className="glass-card rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <Typography 
                      variant="h4" 
                      component="h2" 
                      gutterBottom
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontSize: '1.8rem',
                        color: 'rgba(255, 255, 255, 0.95)',
                        letterSpacing: '0.5px',
                        marginBottom: 0
                      }}
                    >
                      Emergency Contacts
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleAddEmergencyContact}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                        }
                      }}
                    >
                      + Add Contact
                    </Button>
                  </div>
                  
                  {clientDetails.emergencyContacts.length === 0 ? (
                    <div className="glass-card-light p-6 rounded-lg text-center">
                      <p className="theme-text-secondary mb-4">No emergency contacts added yet</p>
                      <Button
                        variant="outlined"
                        onClick={handleAddEmergencyContact}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: '#6366f1',
                          color: '#6366f1',
                          '&:hover': {
                            borderColor: '#4f46e5',
                            backgroundColor: 'rgba(99, 102, 241, 0.04)'
                          }
                        }}
                      >
                        Add Emergency Contact
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {clientDetails.emergencyContacts.map((contact, index) => (
                        <div key={index} className="glass-card-light p-4 rounded-lg">
                          <div className="flex justify-between items-center mb-3">
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }}>
                              Contact {index + 1}
                            </Typography>
                            {clientDetails.emergencyContacts.length > 1 && (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleRemoveEmergencyContact(index)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  minWidth: 'auto',
                                  px: 2
                                }}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium theme-text-primary mb-2">Name</label>
                              <input
                                type="text"
                                value={contact.name}
                                onChange={(e) => handleEmergencyContactChange(index, 'name', e.target.value)}
                                className="theme-input"
                                placeholder="Full name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium theme-text-primary mb-2">Phone</label>
                              <input
                                type="tel"
                                value={contact.phone}
                                onChange={(e) => handleEmergencyContactChange(index, 'phone', e.target.value)}
                                className="theme-input"
                                placeholder="Phone number"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium theme-text-primary mb-2">Relationship</label>
                              <input
                                type="text"
                                value={contact.relationship}
                                onChange={(e) => handleEmergencyContactChange(index, 'relationship', e.target.value)}
                                className="theme-input"
                                placeholder="e.g., Mother, Father"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.emergencyContacts && (
                    <p className="text-red-500 text-sm mt-2">{errors.emergencyContacts}</p>
                  )}
                </div>

                {/* Assessment Status Section */}
                <div className="glass-card rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <Typography 
                      variant="h4" 
                      component="h2" 
                      gutterBottom
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontSize: '1.8rem',
                        color: 'rgba(255, 255, 255, 0.95)',
                        letterSpacing: '0.5px',
                        marginBottom: 0
                      }}
                    >
                      Assessment Status
                    </Typography>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Typography variant="subtitle1" className="mb-2">
                        Would you like to proceed with the assessment?
                      </Typography>
                      <div className="flex gap-4">
                        <Button
                          variant="contained"
                          onClick={() => {
                            setReadyToProceed(true);
                            setWantsToReschedule(false);
                            setWantsToOffboard(false);
                            setClientDetails(prev => ({
                              ...prev,
                              assessmentStatus: 'in_progress'
                            }));
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            background: readyToProceed ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#e0e0e0',
                            color: readyToProceed ? 'white' : '#757575',
                            '&:hover': {
                              background: readyToProceed ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : '#bdbdbd'
                            }
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setWantsToReschedule(true);
                            setReadyToProceed(false);
                            setWantsToOffboard(false);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            background: wantsToReschedule ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '#e0e0e0',
                            color: wantsToReschedule ? 'white' : '#757575',
                            '&:hover': {
                              background: wantsToReschedule ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' : '#bdbdbd'
                            }
                          }}
                        >
                          No, Reschedule
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setWantsToOffboard(true);
                            setReadyToProceed(false);
                            setWantsToReschedule(false);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            background: wantsToOffboard ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : '#e0e0e0',
                            color: wantsToOffboard ? 'white' : '#757575',
                            '&:hover': {
                              background: wantsToOffboard ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' : '#bdbdbd'
                            }
                          }}
                        >
                          No, Offboard
                        </Button>
                      </div>
                    </div>

                    {wantsToReschedule && (
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium theme-text-primary mb-2">
                            New Assessment Date/Time:
                          </label>
                          <div className="relative">
                            <DatePicker
                              selected={clientDetails.rescheduleAssessmentDateTime ? new Date(clientDetails.rescheduleAssessmentDateTime) : null}
                              onChange={(date: Date | null) => {
                                if (date) {
                                  setClientDetails(prev => ({
                                    ...prev,
                                    rescheduleAssessmentDateTime: date.toISOString(),
                                    assessmentStatus: 'rescheduled'
                                  }));
                                } else {
                                  setClientDetails(prev => ({
                                    ...prev,
                                    rescheduleAssessmentDateTime: '',
                                    assessmentStatus: 'not_started'
                                  }));
                                }
                              }}
                              showTimeSelect
                              timeFormat="HH:mm"
                              timeIntervals={15}
                              dateFormat="dd/MM/yyyy HH:mm"
                              className="theme-input w-full p-2 pr-10 border rounded-lg"
                              placeholderText="Select date and time"
                              popperClassName="datepicker-popper"
                              popperPlacement="bottom-start"
                              popperModifiers={[
                                {
                                  name: 'preventOverflow',
                                  options: {
                                    rootBoundary: 'viewport',
                                    tether: false,
                                    altAxis: true,
                                  },
                                },
                              ] as any}
                              minDate={new Date()}
                              minTime={
                                clientDetails.rescheduleAssessmentDateTime && 
                                new Date(clientDetails.rescheduleAssessmentDateTime).toDateString() === new Date().toDateString()
                                  ? new Date()
                                  : new Date(0, 0, 0, 0, 0)
                              }
                              maxTime={new Date(0, 0, 0, 23, 45)}
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                            />
                            <CalendarToday 
                              onClick={() => {
                                const input = document.querySelector('.theme-input[placeholder="Select date and time"]') as HTMLInputElement;
                                if (input) input.focus();
                              }}
                              sx={{ 
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'rgba(0, 0, 0, 0.6)',
                                fontSize: '20px',
                                cursor: 'pointer',
                                '&:hover': {
                                  color: 'rgba(0, 0, 0, 0.9)'
                                }
                              }} 
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium theme-text-primary mb-2">
                            Reschedule Notes:
                          </label>
                          <textarea
                            name="rescheduleNotes"
                            value={clientDetails.rescheduleNotes || ''}
                            onChange={handleInputChange}
                            className="theme-input"
                            rows={4}
                          />
                        </div>
                      </div>
                    )}

                    {wantsToOffboard && (
                      <div className="space-y-4 mt-4">
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <Typography variant="body1" sx={{ color: '#4a5568' }}>
                            As the client does not wish to proceed with or reschedule their assessment, 
                            they will be offboarded. Click "Submit" to proceed to the offboarding page.
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-8">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || (!readyToProceed && !wantsToReschedule && !wantsToOffboard)}
                    startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                      },
                      '&:disabled': {
                        background: '#ccc'
                      }
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                  {showRetryButton && (
                    <Button
                      variant="outlined"
                      onClick={handleRetryCalendarEvent}
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        borderColor: '#ff9800',
                        color: '#ff9800',
                        '&:hover': {
                          borderColor: '#f57c00',
                          backgroundColor: 'rgba(255, 152, 0, 0.04)'
                        },
                        '&:disabled': {
                          borderColor: '#e0e0e0',
                          color: '#9e9e9e'
                        }
                      }}
                    >
                      {isSubmitting ? 'Retrying...' : 'Retry Calendar Event'}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Restore Draft Dialog */}
      <Dialog
        open={showRestoreDraftDialog}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
            Unsaved Work Found
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            We found unsaved assessment data from a previous session. Would you like to restore it?
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This draft was saved automatically to prevent data loss. You can:
          </Typography>
          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
            <li style={{ marginBottom: '8px' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Restore:</strong> Continue where you left off with your previous work
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Discard:</strong> Start fresh with a blank form
              </Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f9fafb' }}>
          <Button
            onClick={handleDiscardDraft}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#9ca3af',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#6b7280',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Discard Draft
          </Button>
          <Button
            onClick={handleRestoreDraft}
            variant="contained"
            autoFocus
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2'
              }
            }}
          >
            Restore Draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
}

export default AssessmentPart1;